import { useState, useEffect, useContext, createContext, createElement } from 'react';
import type { ReactNode } from 'react';
import api from '../services/api';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'reseller-admin' | 'reseller-agent' | 'salam-admin' | 'unknown';
  roles: string[];
  resellerId: string | null;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    isAuthenticated: false,
  });

  useEffect(() => {
    let cancelled = false;
    api
      .get<AuthUser>('/auth/me')
      .then(({ data }) => {
        if (!cancelled) setState({ user: data, loading: false, isAuthenticated: true });
      })
      .catch(() => {
        if (!cancelled) setState({ user: null, loading: false, isAuthenticated: false });
      });
    return () => { cancelled = true; };
  }, []);

  const login = () => {
    window.location.href = `${process.env.REACT_APP_API_URL ?? ''}/api/auth/login`;
  };

  const logout = () => {
    api.post('/auth/logout').finally(() => {
      window.location.href = '/login';
    });
  };

  return createElement(AuthContext.Provider, { value: { ...state, login, logout } }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
